<script>
  import { useNavigate } from 'svelte-navigator';
  import { onMount } from 'svelte';
  import { Moon } from 'svelte-loading-spinners';
  import Form from '../components/form/Form.svelte';
  import { headerDisplayStore } from '../stores/General';
  import Validators from '../utils/validators';
  import AutoCompleteCombo from '../components/form/AutoCompleteCombo.svelte';
  import { create, getById, update } from '../api';
  import PlainInput from "../components/form/PlainInput.svelte";

  const navigate = useNavigate();

  export let id;
  let loading = true;
  let blacklistItem = { approved: true };
  let errors = {};
  let formValid = false;

  let apiModule = 'iab-blacklist';
  let form;
  let rules = {
    publisher_id: {
      validators: [Validators.required, Validators.minLength(2)],
    },
    demand_partner_id: {
      validators: [Validators.required, Validators.minLength(2)],
    },
    iab_category_id: {
      validators: [Validators.required],
    },
  };

  function onCancel() {
    navigate('/iab-blacklist');
  }

  async function getItem() {
    try {
      loading = true;
      const response = await getById(apiModule, id);
      if (response.blacklist) {
        blacklistItem = response.blacklist;
      } else {
        headerDisplayStore.ShowError({
          title: 'Error',
          subtitle: response?.error,
        });
      }
    } catch (error) {
    } finally {
      loading = false;
    }
  }

  onMount(async () => {
    if (id) {
      // get blacklist item by id
      getItem();
      headerDisplayStore.SetTitle('Edit IAB Black-List');
    } else {
      loading = false;
      headerDisplayStore.SetTitle('New IAB Black-List');
    }
    headerDisplayStore.SetBreadCrumbs([
      {
        key: 'blacklist',
        label: 'IAB Black-List',
      },
      {
        key: 'edit',
        label: 'Edit',
      },
    ]);
  });

  function onPublisherChange(event) {
    blacklistItem.publisher_id = event.detail.value;
    blacklistItem = blacklistItem;
  }

  function onDemandPartnerChange(event) {
    blacklistItem.demand_partner_id = event.detail.value;
    blacklistItem = blacklistItem;
  }

  function onIABChange(event) {
    blacklistItem.iab_category_id = event.detail.value;
    blacklistItem = blacklistItem;
  }

  function onApprovedChange(value) {
    blacklistItem.approved = value;
    blacklistItem = blacklistItem;
  }

  function validateInput(data) {
    errors = {};
    if (!data.publisher_id) errors.publisher_id = 'Publisher is required';
    if (!data.demand_partner_id) errors.demand_partner_id = 'Demand Partner is required';
    if (!data.iab_category_id) errors.iab_category_id = 'IAB Category is required';

    if (data.approved == undefined) errors.approved = 'Approved field is required';
    if (Object.keys(errors).length > 0) {
      return false;
    }
    return true;
  }

  function dataInvalid(d) {
    return !d.publisher_id || !d.demand_partner_id || !d.iab_category_id || d.approved == undefined;
  }

  async function onSubmit(e) {
    let values = {
      id: blacklistItem.id,
      publisher_id: blacklistItem.publisher_id,
      demand_partner_id: blacklistItem.demand_partner_id,
      iab_category_id: blacklistItem.iab_category_id,
      approved: blacklistItem.approved,
    };
    const valid = validateInput(values);

    if (valid) {
      try {
        if (id) {
          const response = await update(apiModule, values);
          if (response?.status === 'ok') {
            headerDisplayStore.ShowNotification({
              title: 'Success',
              subtitle: 'IAB Black-List saved successfully',
            });
            navigate('/iab-blacklist');
          } else {
            headerDisplayStore.ShowError({
              title: 'Error',
              subtitle: response?.error,
            });
          }
        } else {
          const response = await create(apiModule, values);
          if (response?.status === 'ok') {
            headerDisplayStore.ShowNotification({
              title: 'Success',
              subtitle: 'IAB Black-List saved successfully',
            });
            navigate('/iab-blacklist');
          } else {
            headerDisplayStore.ShowError({
              title: 'Error',
              subtitle: response?.error,
            });
          }
        }
      } catch (error) {
        headerDisplayStore.ShowError({
          title: 'Server error',
          subtitle: error?.message,
        });
      }
    } else {
      headerDisplayStore.ShowError({
        title: 'Invalid form',
        subtitle: 'Please provid all required fields',
      });
    }
  }

  $: formValid = !dataInvalid(blacklistItem);
</script>

<div class="rounded">
  <div class="py-6">
    <Form {rules} on:submit={onSubmit} bind:this={form}>
      <div class="w-10/12 mx-auto px-4 py-4 sm:px-6 sm:py-6 md:px-8 md:py-8 bg-white rounded-2xl">
        <div>
          {#if !id || (id && !loading && Object.keys(blacklistItem).length)}
            {#if id}
              <PlainInput name="id" type="hidden" value={id} />
            {/if}
            <div class="flex gap-8 w-full">
              <div class="flex flex-col w-full">
                <div>
                  <label
                    for="publisher_id"
                    class="justify-self-end block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2 w-full mb-1"
                  >
                    Publisher
                    <span class="text-red-500">*</span>
                  </label>
                  <AutoCompleteCombo
                    initialValue={blacklistItem?.publisher_id}
                    name="publisher_id"
                    list="publishers"
                    on:change={onPublisherChange}
                    pageSize={20}
                  />
                  {#if errors.publisher_id}
                    <p>{errors.publisher_id}</p>
                  {/if}
                </div>
                <div class="mt-4">
                  <label
                    for="demand_partner_id"
                    class="justify-self-end block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2 w-full mb-1"
                  >
                    Demand Partner
                    <span class="text-red-500">*</span>
                  </label>
                  <AutoCompleteCombo
                    name="demand_partner_id"
                    list="demand_partners"
                    initialValue={blacklistItem.demand_partner_id}
                    on:change={onDemandPartnerChange}
                    pageSize={20}
                  />
                  {#if errors.demand_partner_id}
                    <p>{errors.demand_partner_id}</p>
                  {/if}
                </div>
              </div>
              <div class="flex flex-col w-full">
                <div>
                  <label
                    for="demand_partner_id"
                    class="justify-self-end block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2 w-full mb-1"
                  >
                    IAB Category
                    <span class="text-red-500">*</span>
                  </label>
                  <AutoCompleteCombo
                    name="iab_category_id"
                    list="iab_categories"
                    initialValue={blacklistItem.iab_category_id}
                    on:change={onIABChange}
                    pageSize={100}
                  />
                  {#if errors.iab_category_id}
                    <p>{errors.iab_category_id}</p>
                  {/if}
                </div>
                <div class="mt-4">
                  <label
                    for="approved"
                    class="justify-self-end block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2 w-full mb-1"
                  >
                    Approved
                    <span class="text-red-500">*</span>
                    <div class="flex flex-row pt-3">
                      <div class="mr-8">
                        <input
                          class="mr-2 yes"
                          type="radio"
                          name="approved"
                          checked={blacklistItem.approved}
                          on:click={() => onApprovedChange(true)}
                        /> True
                      </div>
                      <div>
                        <input
                          class="mr-2 no"
                          type="radio"
                          name="approved"
                          checked={!blacklistItem.approved}
                          on:click={() => onApprovedChange(false)}
                        /> False
                      </div>
                    </div>
                  </label>
                  {#if errors.approved}
                    <p>{errors.approved}</p>
                  {/if}
                </div>
              </div>
            </div>
          {:else}
            <div class="min-w-full loader-container text-center">
              <div class="align-middle inline-block h-16">
                <Moon size="4" color="#FF3E00" unit="rem" duration="1s" />
              </div>
              <div class="align-middle inline-block h-full" />
            </div>
          {/if}
        </div>
      </div>
      <div class="flex flex-row justify-end gap-4 mt-8 w-10/12 mx-auto">
        <button
          disabled={!formValid}
          type="submit"
          class="w-48 h-12 relative inline-flex items-center justify-center px-1 py-1 rounded-md 
          border shadow-nv border-custom-orange bg-custom-orange text-sm font-medium text-white 
          hover:bg-custom-orange focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-600 
          focus:border-blue-600 focus:ring-offset-2 focus:bg-orange-500"
        >
          <span>{id ? 'Save' : 'Create'}</span>
        </button>
        <button
          on:click={onCancel}
          type="button"
          class="w-40 h-12 relative inline-flex items-center justify-center px-1 py-1 rounded-md 
          border shadow-nv border-custom-orange text-sm font-medium text-custom-orange 
          hover:text-white hover:bg-custom-orange focus:z-10 focus:outline-none focus:ring-1 
          focus:ring-blue-600 focus:border-blue-600 focus:ring-offset-2 focus:bg-orange-500"
        >
          <span>Cancel</span>
        </button>
      </div>
    </Form>
  </div>
</div>

<style>
  .yes:not(:checked) {
    border-width: 2px;
    border-color: #009834;
  }
  .yes:checked {
    background-color: #009834;
  }
  .no:not(:checked) {
    border-width: 2px;
    border-color: #d60000;
  }
  .no:checked {
    background-color: #d60000;
  }
</style>
